import { AttachIcon } from '@synergy/theme';
import { Box, Divider, IconButton, Stack, Theme } from '@mui/material';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Fragment } from 'react';
import { Media, UploadState } from '@synergy/models';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import urljoin from 'url-join';

import { PoolButton } from '../button/pool-button';
import { PoolLinearProgress } from '../linear-progress/pool-linear-progress';
import { PoolTypography } from '../typography/pool-typography';

const useStyles = makeStyles((theme: Theme) => ({
    fileList: {
        padding: 0,
        margin: theme.spacing(1.5, 0),
        listStyle: 'none',
        // PALO body scale; the list sat two steps above everything around it.
        fontSize: 12.5,
        fontFamily: theme.typography.fontFamily,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        backgroundColor: theme.palette.background.paper,
    },
    attachIcon: {
        marginRight: theme.spacing(),

        fontSize: 15,
    },
    createDate: {
        marginRight: theme.spacing(),
        marginLeft: theme.spacing(5),
        color: theme.palette.text.primary,
    },
    fileContainer: {
        margin: theme.spacing(0, 1),
        display: 'flex',
        alignItems: 'center',
        minHeight: theme.spacing(6),

        '& a': {
            color: theme.palette.text.primary,
            textDecoration: 'none',
            alignItems: 'center',
        },

        '& a > p, a, span': {
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
        },
    },
    link: {
        display: 'flex',
        width: '100%',

        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
    deleteButton: {
        marginLeft: 'auto',
        minWidth: 38,

        '& svg': {
            transform: 'rotate(45deg)',
            fontSize: 16,
        },
    },
    divider: {
        borderColor: theme.palette.grey[300],

        '&:last-child': {
            display: 'none',
        },
    },
    linearProgress: {
        marginLeft: theme.spacing(),
        marginRight: theme.spacing(3),
    },
    mediaDate: {
        color: theme.palette.grey[500],
    },
}));

interface PoolFileListProps {
    downloadBaseUrl?: string;
    acceptedFiles?: Media[];
    onDeleteClicked?: (file: Media) => void;
    onCancelClicked?: (file: Media) => void;
    readOnly?: boolean;
    showCreateDate?: boolean;
    target?: string;
    hideTitle?: boolean;
    directDownload?: boolean;
}

export const PoolFileList = (props: PoolFileListProps) => {
    const classes = useStyles();
    const { acceptedFiles, downloadBaseUrl, target, hideTitle, directDownload } = props;

    const handleDeleteClicked = (media: Media) => () => {
        if (props.onDeleteClicked) {
            props.onDeleteClicked(media);
        }
    };

    const handleCancelClicked = (media: Media) => () => {
        if (props.onCancelClicked) {
            props.onCancelClicked(media);
        }
    };

    if (!acceptedFiles || acceptedFiles.length === 0) {
        return null;
    }

    const renderFileDetail = (media: Media) => {
        switch (media.state) {
            case UploadState.InProgress: {
                return (
                    <>
                        <PoolLinearProgress
                            variant="determinate"
                            value={media.progress || 0}
                            className={classes.linearProgress}
                        />
                        {props.onCancelClicked && (
                            <PoolButton
                                className={classes.deleteButton}
                                color="primary"
                                onClick={handleCancelClicked(media)}
                            >
                                <AddIcon />
                            </PoolButton>
                        )}
                    </>
                );
            }

            case UploadState.Finished: {
                return (
                    <>
                        {downloadBaseUrl && media.id >= 0 ? (
                            <a
                                href={urljoin(downloadBaseUrl, media.pathname)}
                                download={directDownload ?? true ? media.originalName : undefined}
                                target={target || '_blank'}
                                className={classes.link}
                            >
                                <AttachIcon className={classes.attachIcon} />
                                <Stack>
                                    <PoolTypography typography="body1">
                                        {media.originalName ?? media.pathname}
                                    </PoolTypography>
                                    <PoolTypography typography="caption" className={classes.mediaDate}>
                                        <FormattedDate value={media.createdAt} dateStyle="medium" timeStyle="medium" />
                                    </PoolTypography>
                                </Stack>
                            </a>
                        ) : (
                            <>
                                <AttachIcon className={classes.attachIcon} />
                                <span>{media.originalName ?? media.pathname}</span>
                            </>
                        )}
                        {!props.readOnly && props.onDeleteClicked && (
                            <IconButton
                                color="primary"
                                onClick={handleDeleteClicked(media)}
                                className={classes.deleteButton}
                            >
                                <AddIcon />
                            </IconButton>
                        )}
                    </>
                );
            }

            case UploadState.Failed: {
                return (
                    <>
                        <Stack color="error">
                            <Box component="span">{media.originalName}</Box>
                            <FormattedMessage id="components.fileList.upload.failed" />
                        </Stack>
                        <IconButton className={classes.deleteButton} color="error" onClick={handleDeleteClicked(media)}>
                            <DeleteForeverIcon />
                        </IconButton>
                    </>
                );
            }

            default:
                return undefined;
        }
    };

    return (
        <>
            {!hideTitle && (
                <PoolTypography color="grey.500" typography="caption" id="components.fileList.attachedFiles" />
            )}
            <ul className={classes.fileList}>
                {!!acceptedFiles &&
                    acceptedFiles.map(file => {
                        return (
                            <Fragment key={`${file.pathname}_${file.size}`}>
                                <li className={classes.fileContainer}>{renderFileDetail(file)}</li>
                                <Divider className={classes.divider} />
                            </Fragment>
                        );
                    })}
            </ul>
        </>
    );
};
